import type {
  DataGridProProps,
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid-pro';
import { type UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import type { ItemStubsResponseDTO, TableLayoutDTO } from '@tempo-io/roadmap-dto';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@tempo-io/sui-accordion';
import { SystemStatusBadge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import {
  Add,
  Dependency,
  DoNotDisturbOn,
  ErrorIndicator,
  Search,
  Warning,
} from '@tempo-io/sui-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@tempo-io/sui-popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ItemPicker } from '@/components/common/custom-field-value/ItemPicker';
import { ItemsTable } from '@/components/common/items-table/ItemsTable';
import type { ItemsTableRow } from '@/components/common/items-table/types';
import { INSIGHTS_STALE_TIME } from '@/components/loop-dashboard/data/constants';
import { HealthBadgeWithTooltip } from '@/components/loop-dashboard/shared/HealthBadgeWithTooltip';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { ItemsApi } from '@/lib/api/common/items';
import { HealthApi } from '@/lib/api/dashboard/health';
import { extractServerMessage } from '@/lib/api/extractServerMessage';

import {
  type ItemHealth,
  ItemHealthLevel,
  isItemDependencyBlocked,
} from '@/lib/api/types/dashboard/health';

import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { ItemStatus } from '@/lib/api/types/itemStatus';
import type { Item } from '@/lib/api/types/items';
import type { ItemType, ItemTypeMap } from '@/lib/api/types/itemType';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import {
  InvalidDependencyPairError,
  ReadOnlyAdapterError,
  SelfDependencyError,
} from '@/lib/mutations/item-store/dependencyErrors';
import { useItemDependenciesPanelQuery } from '@/lib/queries/common/useItemDependenciesPanelQuery';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { insightsQueryKeys } from '@/lib/query-keys/insights';
import { TabValue } from '@/lib/types/itemPanel';
import { associateById } from '@/lib/utils/common/associateById';
import { formatLocalDate } from '@/lib/utils/common/dateFormats';
import type { DependencyTabAdapter } from '../adapters/types';
import { useTabAdapter } from '../context/TabAdapterContext';
import type { DependencyForecastDates } from '../hooks/useDependencyForecastDates';
import { useDependencyForecastDates } from '../hooks/useDependencyForecastDates';

type ItemWithSchedule = Item & {
  forecastedStartDateTime: string | null;
  forecastedFinishDateTime: string | null;
};

// Item's `forecastedStart/FinishDateTime` are merged onto ItemsTableRow at runtime via
// the `items` prop (see ItemWithSchedule), but aren't declared on ItemsTableRow's type.
type DependencyRow = ItemsTableRow &
  Pick<ItemWithSchedule, 'forecastedStartDateTime'> &
  Pick<ItemWithSchedule, 'forecastedFinishDateTime'>;

type DependencySectionValue = 'predecessors' | 'successors';

const DEPENDENCY_DROPDOWN_ITEM_LIMIT = 50;
const MIN_NAME_COLUMN_WIDTH = 160;

// The column menu is a MUI Popper portaled to document.body. This tab's Sheet
// sets `pointer-events: none` on body while open (Radix only re-enables it for
// the Sheet's own content subtree), so the portaled menu inherits that and
// clicks pass through to the grid rows underneath unless re-enabled explicitly.

// Ephemeral layout for the embedded ItemsTable: only the name column is
// layout-driven (via groupingColDef); every other column is hand-built in
// buildColumns below, so visualizationId/itemLevelId are never persisted or read.
const DEPENDENCY_TABLE_LAYOUT: TableLayoutDTO = {
  visualizationType: 'table',
  visualizationId: 1,
  itemLevelId: 1,
  theme: 'standard',
  density: 'compact',
  columns: [{ type: 'name', customFieldId: null }],
};

const toItemStubs = (rows: ItemWithSchedule[]): ItemStubsResponseDTO =>
  rows.map((row) => ({ id: row.id, matched: true }));

const withSchedules = (
  items: Item[],
  forecastDates: Record<number, DependencyForecastDates>,
): ItemWithSchedule[] =>
  items.map((dep) => ({
    ...dep,
    forecastedStartDateTime: forecastDates[dep.id]?.forecastedStartDateTime ?? null,
    forecastedFinishDateTime: forecastDates[dep.id]?.forecastedFinishDateTime ?? null,
  }));

const sortByDate = (
  items: ItemWithSchedule[],
  dateField: 'forecastedStartDateTime' | 'forecastedFinishDateTime',
  sortDirection: 1 | -1,
): ItemWithSchedule[] =>
  [...items].sort((a, b) => {
    const aDate = a[dateField];
    const bDate = b[dateField];
    if (aDate === null && bDate === null) return 0;
    if (aDate === null) return -1;
    if (bDate === null) return 1;
    return aDate < bDate ? -sortDirection : aDate > bDate ? sortDirection : 0;
  });

// Matches the header look of the old sui-tablelayout table (uppercase, semibold,
// secondary-grey) since ItemsTable's default MUI header styling doesn't apply it.
// The tree-data grouping (name) column also keeps a right border/divider even with
// showColumnVerticalBorder/showCellVerticalBorder off, since MUI treats it like a
// pinned column boundary — strip it explicitly by field. Excludes the `--sorted`
// header state so it still gets the theme's accent-blue sort-highlight shadow.
const DEPENDENCY_TABLE_SX = {
  '& .MuiDataGrid-columnHeaderTitle': {
    textTransform: 'uppercase',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: 'var(--color-text-neutral-secondary)',
  },
  '& .MuiDataGrid-columnHeader[data-field="__tree_data_group__"]:not(.MuiDataGrid-columnHeader--sorted), & .MuiDataGrid-cell[data-field="__tree_data_group__"]':
    {
      borderRight: 'none',
      boxShadow: 'none',
    },
} as const;

type AddFlow =
  | { kind: 'idle' }
  | { kind: 'open'; section: DependencySectionValue }
  | { kind: 'submitting'; section: DependencySectionValue }
  | { kind: 'error'; section: DependencySectionValue; message: string };

type RemoveFlow =
  | { kind: 'idle' }
  | { kind: 'submitting'; section: DependencySectionValue; itemId: number }
  | { kind: 'error'; section: DependencySectionValue; itemId: number; message: string };

const StatusCell = ({ row, itemStatuses }: { row: ItemsTableRow; itemStatuses: ItemStatus[] }) => {
  const itemStatus = itemStatuses.find((status) => status.id === row.itemStatusId);
  if (!itemStatus) {
    return null;
  }
  return <ItemStatusBadge color={itemStatus.color} label={itemStatus.name} />;
};

const HealthCell = ({ health }: { health: ItemHealth | undefined }) => {
  if (!health) {
    return null;
  }
  return (
    <HealthBadgeWithTooltip
      healthLevel={health.healthLevel}
      healthTypes={health.risks.map((risk) => risk.key)}
      className="max-w-full"
    />
  );
};

const DependencyPredecessorCell = ({
  row,
  currentItemHealth,
}: {
  row: ItemsTableRow;
  currentItemHealth: ItemHealth | undefined;
}) => {
  const { t } = useTranslation();
  const isBlocked =
    currentItemHealth?.dependency?.blockedByPredecessorIds.includes(row.id) ?? false;
  const isAtRisk =
    !isBlocked && (currentItemHealth?.dependency?.atRiskPredecessorIds.includes(row.id) ?? false);

  if (!isBlocked && !isAtRisk) {
    return null;
  }

  const tooltip = isBlocked
    ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blockedTooltip)
    : t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.atRiskTooltip);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={tooltip}
            className="flex w-full cursor-default items-center"
          >
            {isBlocked ? (
              <ErrorIndicator className="size-4 text-icon-error" />
            ) : (
              <Warning className="size-4 text-icon-warning" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const DependencySuccessorCell = ({
  row,
  currentItemId,
  healthByItemId,
}: {
  row: ItemsTableRow;
  currentItemId: number;
  healthByItemId: Map<number, ItemHealth | undefined>;
}) => {
  const { t } = useTranslation();
  const rowHealth = healthByItemId.get(row.id);
  const isBlocking =
    rowHealth?.dependency?.blockedByPredecessorIds.includes(currentItemId) ?? false;
  const isAtRisk =
    !isBlocking && (rowHealth?.dependency?.atRiskPredecessorIds.includes(currentItemId) ?? false);

  if (!isBlocking && !isAtRisk) {
    return null;
  }

  const tooltip = isBlocking
    ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blockingTooltip)
    : t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.atRiskBlockingTooltip);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={tooltip}
            className="flex w-full cursor-default items-center"
          >
            {isBlocking ? (
              <ErrorIndicator className="size-4 text-icon-error" />
            ) : (
              <Warning className="size-4 text-icon-warning" />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const HEALTH_LEVEL_SORT_RANK: Record<ItemHealthLevel, number> = {
  [ItemHealthLevel.DONE]: 0,
  [ItemHealthLevel.HEALTHY]: 1,
  [ItemHealthLevel.NEEDS_ATTENTION]: 2,
  [ItemHealthLevel.ACTION_REQUIRED]: 3,
};

const NoDependenciesOverlay = () => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center py-4 text-center text-sm text-text-neutral-secondary">
      {t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.noDependencies)}
    </div>
  );
};

const DependencyNameCell = ({ row, itemTypes }: { row: ItemsTableRow; itemTypes: ItemType[] }) => {
  const { openItemPanel } = useItemPanelNavigation();
  const itemType = itemTypes.find((type) => type.id === row.itemTypeId);
  return (
    <button
      type="button"
      onClick={() => openItemPanel({ itemId: row.id, panelTab: TabValue.DEPENDENCIES })}
      className="flex w-full min-w-0 cursor-pointer items-center gap-2 text-left hover:underline"
    >
      {!!itemType && <ItemTypeIcon itemType={itemType} />}
      <span className="flex-1 min-w-0 truncate font-medium leading-5 text-text-neutral-primary">
        {row.name}
      </span>
    </button>
  );
};

const buildGroupingColDef = (
  nameHeader: string,
  itemTypes: ItemType[],
): DataGridProProps<ItemsTableRow>['groupingColDef'] => ({
  headerName: nameHeader,
  valueGetter: (_value, row: ItemsTableRow) => row.name,
  renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
    <DependencyNameCell row={params.row} itemTypes={itemTypes} />
  ),
  minWidth: MIN_NAME_COLUMN_WIDTH,
  flex: 1,
  hideDescendantCount: true,
  hideable: false,
  sortable: true,
  disableColumnMenu: false,
  pinnable: false,
});

interface AddDependencyControlProps {
  addLabel: string;
  pickerFilter: ItemFiltersType;
  excludedItemIds: number[];
  itemTypesById: ItemTypeMap;
  isAdding: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  eagerLoad: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onSelect: (pickedItemId: number) => void;
}

const AddDependencyControl: React.FC<AddDependencyControlProps> = ({
  addLabel,
  pickerFilter,
  excludedItemIds,
  itemTypesById,
  isAdding,
  isSubmitting,
  errorMessage,
  eagerLoad,
  onOpen,
  onCancel,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <Popover
      open={isAdding}
      onOpenChange={(next) => {
        if (next) {
          onOpen();
        } else if (!isSubmitting) {
          onCancel();
        }
      }}
    >
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="default" size="icon-sm" aria-label={addLabel} className="shrink-0">
                <Add />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>{addLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent
        align="end"
        className="w-72 border-0 bg-transparent p-0 shadow-none"
        aria-label={addLabel}
      >
        <ItemPicker
          value={null}
          onChange={(picked) => {
            if (picked) {
              onSelect(picked.id);
            }
          }}
          itemTypesById={itemTypesById}
          filter={pickerFilter}
          excludedItemIds={excludedItemIds}
          placeholder={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.pickerPlaceholder)}
          autoFocus
          disabled={isSubmitting}
          isClearable={false}
          withExternalKey
          persistInput
          eagerLoad={eagerLoad}
          leadingIcon={<Search className="size-6" />}
        />
        {!!errorMessage && (
          <span
            role="alert"
            aria-live="polite"
            className="text-xs text-text-error"
            data-testid="add-dependency-error"
          >
            {errorMessage}
          </span>
        )}
      </PopoverContent>
    </Popover>
  );
};

interface DependencySectionProps {
  value: DependencySectionValue;
  title: string;
  description?: string;
  blockedBadgeLabel: string | null;
  groupingColDef: DataGridProProps<ItemsTableRow>['groupingColDef'];
  columns: GridColDef<ItemsTableRow>[];
  rows: ItemWithSchedule[];
  isLoading?: boolean;
  rowClassName?: (params: GridRowClassNameParams<ItemsTableRow>) => string;
  addControl?: React.ReactNode;
  removeError?: string | null;
}

const DependencySection: React.FC<DependencySectionProps> = ({
  value,
  title,
  description,
  blockedBadgeLabel,
  groupingColDef,
  columns,
  rows,
  isLoading,
  rowClassName,
  addControl,
  removeError,
}) => {
  const { t } = useTranslation();
  const dataset = useMemo(() => toItemStubs(rows), [rows]);
  return (
    <AccordionItem
      value={value}
      className="rounded-lg border border-border-neutral bg-bg-surface-base px-4 last:border"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <AccordionTrigger className="w-full text-sm font-semibold leading-5 text-text-neutral-primary hover:no-underline [&>svg]:hidden">
            <span className="flex flex-1 items-start gap-2">
              <span className="flex-1">
                {title} ({rows.length})
                {!!description && (
                  <span className="mt-1 block text-sm font-normal leading-5 text-text-neutral-secondary">
                    {description}
                  </span>
                )}
              </span>
              {!!blockedBadgeLabel && (
                <SystemStatusBadge variant="error" className="p-2">
                  {blockedBadgeLabel}
                </SystemStatusBadge>
              )}
            </span>
          </AccordionTrigger>
        </div>
        {addControl}
        <AccordionTrigger
          aria-label={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.toggleSection)}
          className="shrink-0 py-0"
        />
      </div>
      <AccordionContent className="pt-2">
        <div className="border-t border-border-neutral max-h-[15rem] overflow-y-auto">
          <ItemsTable
            dataset={dataset}
            items={rows}
            layout={DEPENDENCY_TABLE_LAYOUT}
            customGroupingColDef={groupingColDef}
            customColumns={columns}
            checkboxSelection={false}
            disableRowReordering
            readOnly
            sortingMode="client"
            disableColumnSorting={false}
            disableColumnMenu={false}
            slotProps={{
              basePopper: { style: { pointerEvents: 'auto' } },
            }}
            loading={isLoading}
            density="comfortable"
            showColumnVerticalBorder={false}
            showCellVerticalBorder={false}
            sx={DEPENDENCY_TABLE_SX}
            autoHeight
            getRowClassName={rowClassName}
            slots={{ noRowsOverlay: NoDependenciesOverlay }}
            fetchHealth={false}
            fetchSchedules={false}
          />
        </div>
        {!!removeError && (
          <span
            role="alert"
            aria-live="polite"
            className="mt-2 block text-xs text-text-error"
            data-testid="remove-dependency-error"
          >
            {removeError}
          </span>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

interface DependenciesProps {
  item: Item;
}

export const useItemHealthByItemId = (itemIds: number[]): Map<number, ItemHealth | undefined> =>
  useQueries({
    queries: itemIds.map(
      (id): UseQueryOptions<ItemHealth | undefined> => ({
        queryKey: insightsQueryKeys.health.byItemId(id),
        queryFn: async () => {
          const response = await HealthApi.getItemHealth({ itemIds: [id] });
          return response.data[0];
        },
        staleTime: INSIGHTS_STALE_TIME,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
      }),
    ),
    combine: (results) => new Map(itemIds.map((id, itemIndex) => [id, results[itemIndex]?.data])),
  });

// 'group' lets the per-row remove button (opacity-0 by default) reveal itself
// via group-hover — mirrors the row-level class the old <TableRow> hardcoded.
const blockedRowClassName =
  (blockedIds: Set<number>) => (params: GridRowClassNameParams<ItemsTableRow>) =>
    blockedIds.has(Number(params.id))
      ? 'group bg-bg-error-subtler hover:bg-bg-error-subtler-hovered'
      : 'group';

const DEFAULT_OPEN_SECTIONS: DependencySectionValue[] = ['predecessors', 'successors'];

export const Dependencies = ({ item }: DependenciesProps) => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<DependencyTabAdapter>();
  const {
    itemStatuses,
    itemTypes,
    predecessors,
    successors,
    isPredecessorsLoading,
    isSuccessorsLoading,
  } = useItemDependenciesPanelQuery(item);
  const predecessorIds = useMemo(() => predecessors.map((p) => p.id), [predecessors]);
  const successorIds = useMemo(() => successors.map((s) => s.id), [successors]);
  const allDependencyIds = useMemo(
    () => [...predecessorIds, ...successorIds],
    [predecessorIds, successorIds],
  );
  const forecastDates = useDependencyForecastDates(allDependencyIds, adapter.scenarioContext);
  const healthByItemId = useItemHealthByItemId(allDependencyIds);
  const blockedIds = useMemo(
    () =>
      new Set(
        allDependencyIds.filter((id) => {
          const health = healthByItemId.get(id);
          return health ? isItemDependencyBlocked(health) : false;
        }),
      ),
    [allDependencyIds, healthByItemId],
  );
  const currentItemHealth = useItemHealthByItemId([item.id]).get(item.id);
  const isCurrentItemBlocked = currentItemHealth
    ? isItemDependencyBlocked(currentItemHealth)
    : false;

  const itemTypesById = useMemo<ItemTypeMap>(() => associateById(itemTypes), [itemTypes]);
  const excludedItemIds = useMemo(
    () => [item.id, ...predecessorIds, ...successorIds],
    [item.id, predecessorIds, successorIds],
  );
  const canAdd = adapter.canAddDependency() && adapter.loopId != null;
  const canRemove = adapter.isEditable();
  const pickerFilter = useMemo<ItemFiltersType>(
    () => (adapter.loopId != null ? { loopId: { value: [adapter.loopId], operator: 'eq' } } : {}),
    [adapter.loopId],
  );

  const { data: candidateCount } = useQuery({
    queryKey: commonQueryKeys.items.byFilters({
      filter: pickerFilter,
      limit: 1,
      hierarchical: false,
    }),
    queryFn: () => ItemsApi.getItems({ filter: pickerFilter, limit: 1, hierarchical: false }),
    select: (response) => response.recordCount,
    enabled: canAdd,
    staleTime: INSIGHTS_STALE_TIME,
  });
  const showEagerDropdown =
    candidateCount != null &&
    candidateCount - excludedItemIds.length <= DEPENDENCY_DROPDOWN_ITEM_LIMIT;

  const [flow, setFlow] = useState<AddFlow>({ kind: 'idle' });
  const [removeFlow, setRemoveFlow] = useState<RemoveFlow>({ kind: 'idle' });

  const dependencyValidationMessage = (error: unknown): string | null => {
    if (error instanceof SelfDependencyError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.selfDependencyError);
    }

    if (error instanceof InvalidDependencyPairError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.invalidDependencyPairError);
    }

    if (error instanceof ReadOnlyAdapterError) {
      return t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.readOnlyError);
    }

    return null;
  };

  const submit = async (section: DependencySectionValue, pickedItemId: number): Promise<void> => {
    const [predecessorId, successorId] =
      section === 'predecessors' ? [pickedItemId, item.id] : [item.id, pickedItemId];
    setFlow({ kind: 'submitting', section });
    try {
      await adapter.addDependency(predecessorId, successorId);
      setFlow({ kind: 'idle' });
    } catch (error) {
      const message =
        dependencyValidationMessage(error) ??
        (await extractServerMessage(error)) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addError);
      setFlow({ kind: 'error', section, message });
    }
  };

  const remove = async (section: DependencySectionValue, rowItemId: number): Promise<void> => {
    if (removeFlow.kind === 'submitting') return;
    const [predecessorId, successorId] =
      section === 'predecessors' ? [rowItemId, item.id] : [item.id, rowItemId];
    setRemoveFlow({ kind: 'submitting', section, itemId: rowItemId });
    try {
      await adapter.removeDependency(predecessorId, successorId);
      setRemoveFlow({ kind: 'idle' });
    } catch (error) {
      const message =
        dependencyValidationMessage(error) ??
        (await extractServerMessage(error)) ??
        t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.removeError);
      setRemoveFlow({ kind: 'error', section, itemId: rowItemId, message });
    }
  };

  // Memoized so identity stays stable across incidental re-renders (e.g. health
  // polling) — otherwise a fresh rows array on every render resets the grid's
  // client-side sort state right after the user picks Asc/Desc from the column menu.
  const predecessorRows = useMemo(
    () => sortByDate(withSchedules(predecessors, forecastDates), 'forecastedFinishDateTime', -1),
    [predecessors, forecastDates],
  );
  const successorRows = useMemo(
    () => sortByDate(withSchedules(successors, forecastDates), 'forecastedStartDateTime', 1),
    [successors, forecastDates],
  );

  const isRemoveInFlight = removeFlow.kind === 'submitting';

  const buildColumns = (section: DependencySectionValue): GridColDef<ItemsTableRow>[] => {
    const columns: GridColDef<ItemsTableRow>[] = [
      ...(section === 'predecessors' || section === 'successors'
        ? [
            {
              field: 'dependencyStatus',
              headerName: '',
              width: 56,
              sortable: false,
              disableColumnMenu: true,
              renderHeader: () => (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t(($) => $.adaptivePlanner.itemPanel.dependencies)}
                        className="flex w-full cursor-default items-center justify-center text-text-brand-secondary"
                      >
                        <Dependency />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {t(($) => $.adaptivePlanner.itemPanel.dependencies)}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ),
              renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
                section === 'predecessors' ? (
                  <DependencyPredecessorCell
                    row={params.row}
                    currentItemHealth={currentItemHealth}
                  />
                ) : (
                  <DependencySuccessorCell
                    row={params.row}
                    currentItemId={item.id}
                    healthByItemId={healthByItemId}
                  />
                ),
            } satisfies GridColDef<ItemsTableRow>,
          ]
        : []),
      {
        field: 'status',
        headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.status),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: ItemsTableRow) =>
          itemStatuses.find((itemStatus) => itemStatus.id === row.itemStatusId)?.priority ?? '',
        renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
          <StatusCell row={params.row} itemStatuses={itemStatuses} />
        ),
      },
      {
        field: 'health',
        headerName: t(($) => $.views.table.columns.systemFields.health),
        width: 140,
        sortable: true,
        valueGetter: (_value, row: ItemsTableRow) =>
          HEALTH_LEVEL_SORT_RANK[healthByItemId.get(row.id)?.healthLevel ?? ItemHealthLevel.DONE],
        renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
          <HealthCell health={healthByItemId.get(params.row.id)} />
        ),
      },
      section === 'successors'
        ? {
            field: 'plannedStart',
            headerName: t(($) => $.views.table.columns.systemFields.plannedStartDate),
            width: 140,
            sortable: true,
            valueGetter: (_value, row: ItemsTableRow) => row.plannedStartDateTime ?? '',
            renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
              params.row.plannedStartDateTime
                ? formatLocalDate(params.row.plannedStartDateTime)
                : '-',
          }
        : {
            field: 'plannedFinish',
            headerName: t(($) => $.views.table.columns.systemFields.plannedFinishDate),
            width: 140,
            sortable: true,
            valueGetter: (_value, row: ItemsTableRow) => row.plannedFinishDateTime ?? '',
            renderCell: (params: GridRenderCellParams<ItemsTableRow>) =>
              params.row.plannedFinishDateTime
                ? formatLocalDate(params.row.plannedFinishDateTime)
                : '-',
          },
      section === 'successors'
        ? {
            field: 'forecastedStart',
            headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedStart),
            width: 170,
            sortable: true,
            valueGetter: (_value, row: ItemsTableRow) =>
              (row as DependencyRow).forecastedStartDateTime ?? '',
            renderCell: (params: GridRenderCellParams<ItemsTableRow>) => {
              const row = params.row as DependencyRow;
              return row.forecastedStartDateTime
                ? formatLocalDate(row.forecastedStartDateTime)
                : '-';
            },
          }
        : {
            field: 'forecastedFinish',
            headerName: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.forecastedFinish),
            width: 170,
            sortable: true,
            valueGetter: (_value, row: ItemsTableRow) =>
              (row as DependencyRow).forecastedFinishDateTime ?? '',
            renderCell: (params: GridRenderCellParams<ItemsTableRow>) => {
              const row = params.row as DependencyRow;
              return row.forecastedFinishDateTime
                ? formatLocalDate(row.forecastedFinishDateTime)
                : '-';
            },
          },
    ];
    if (canRemove) {
      columns.push({
        field: 'actions',
        headerName: '',
        width: 48,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params: GridRenderCellParams<ItemsTableRow>) => (
          <div className="flex justify-end pr-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void remove(section, params.row.id);
                    }}
                    disabled={isRemoveInFlight}
                    aria-label={t(
                      ($) =>
                        $.adaptivePlanner.itemPanel.dependenciesSection.removeDependencyAriaLabel,
                      { itemName: params.row.name ?? '' },
                    )}
                    className="shrink-0 text-text-neutral-secondary opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                  >
                    <DoNotDisturbOn />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t(($) => $.common.remove)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      });
    }
    return columns;
  };

  const sections = [
    {
      value: 'predecessors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessors),
      description: t(
        ($) => $.adaptivePlanner.itemPanel.dependenciesSection.predecessorsDescription,
      ),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addPredecessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.waitingOn),
      rows: predecessorRows,
      isLoading: isPredecessorsLoading,
    },
    {
      value: 'successors' as const,
      title: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successors),
      description: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.successorsDescription),
      addLabel: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.addSuccessor),
      nameHeader: t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.completionEnables),
      rows: successorRows,
      isLoading: isSuccessorsLoading,
    },
  ];

  const renderAddControl = (section: DependencySectionValue, addLabel: string): React.ReactNode => {
    if (!canAdd) {
      return null;
    }
    const isAdding = flow.kind !== 'idle' && flow.section === section;
    return (
      <AddDependencyControl
        addLabel={addLabel}
        pickerFilter={pickerFilter}
        excludedItemIds={excludedItemIds}
        itemTypesById={itemTypesById}
        isAdding={isAdding}
        isSubmitting={isAdding && flow.kind === 'submitting'}
        errorMessage={isAdding && flow.kind === 'error' ? flow.message : null}
        eagerLoad={showEagerDropdown}
        onOpen={() => setFlow({ kind: 'open', section })}
        onCancel={() => setFlow({ kind: 'idle' })}
        onSelect={(pickedItemId) => {
          void submit(section, pickedItemId);
        }}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Accordion
        type="multiple"
        defaultValue={DEFAULT_OPEN_SECTIONS}
        className="flex flex-col gap-4"
      >
        {sections.map((section) => {
          const rows = section.rows;
          const blockedBadgeLabel =
            section.value === 'predecessors'
              ? isCurrentItemBlocked
                ? t(($) => $.insights.dependency.blocked)
                : null
              : (() => {
                  const blockingCount = rows.filter((row) => blockedIds.has(row.id)).length;
                  return blockingCount > 0
                    ? t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.blocking, {
                        count: blockingCount,
                      })
                    : null;
                })();
          return (
            <DependencySection
              key={section.value}
              value={section.value}
              title={section.title}
              description={section.description}
              blockedBadgeLabel={blockedBadgeLabel}
              groupingColDef={buildGroupingColDef(section.nameHeader, itemTypes)}
              columns={buildColumns(section.value)}
              rows={rows}
              isLoading={section.isLoading}
              rowClassName={blockedRowClassName(blockedIds)}
              addControl={renderAddControl(section.value, section.addLabel)}
              removeError={
                removeFlow.kind === 'error' && removeFlow.section === section.value
                  ? removeFlow.message
                  : null
              }
            />
          );
        })}
      </Accordion>
    </div>
  );
};
